import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { Avatar, Badge, Typography } from "antd";
import { UserOutlined, PhoneOutlined, PauseCircleOutlined, CheckCircleOutlined, StopOutlined, ClockCircleOutlined, ArrowDownOutlined, ArrowUpOutlined } from "@ant-design/icons";

import "./AgentStatus.css";

const { Text } = Typography;

const formatDuration = (seconds) => {
  if (seconds === undefined || seconds === null || isNaN(seconds)) return "00:00";
  const secNum = parseInt(seconds, 10);
  const hours = Math.floor(secNum / 3600);
  const minutes = Math.floor((secNum - (hours * 3600)) / 60);
  const secs = secNum - (hours * 3600) - (minutes * 60);

  const hDisplay = hours > 0 ? (hours < 10 ? "0" + hours : hours) + ":" : "";
  const mDisplay = (minutes < 10 ? "0" + minutes : minutes) + ":";
  const sDisplay = (secs < 10 ? "0" + secs : secs);
  return hDisplay + mDisplay + sDisplay;
};

const getStatusInfo = (item) => {
  if (item.status == 5) {
    return { class: "unavailable", tag: "tag-unavailable", icon: <StopOutlined />, label: "Unavailable" };
  } else if (item.status == 6) {
    return { class: "ringing", tag: "tag-ringing", icon: <PhoneOutlined />, label: "Ringing" };
  } else if (item.status == 2 || item.status == 3) {
    return { class: "incall", tag: "tag-incall", icon: <PhoneOutlined />, label: "In Call" };
  } else if (item.paused == 1) {
    return { class: "paused", tag: "tag-paused", icon: <PauseCircleOutlined />, label: "Paused" };
  } else if (item.status === "1" && item.paused === "0") {
    return { class: "online", tag: "tag-online", icon: <CheckCircleOutlined />, label: "Available" };
  } else {
    return { class: "idle", tag: "tag-idle", icon: <ClockCircleOutlined />, label: "Idle" };
  }
};

const AgentAvatar = React.memo(({ item }) => {
  const [imageError, setImageError] = useState(false);
  const avatarVersion = useSelector((state) => state.loginReducer.avatarVersion);

  const endpointId = item.endpoint_id;
  const company_id = item.company_id;
  const avatarUrl = (company_id && endpointId)
    ? `${process.env.REACT_APP_API_URL}/records/${company_id}/images/${endpointId}.jpg?v=${avatarVersion}`
    : null;

  let badgeStatus = "default";
  if (item.status == 5) badgeStatus = "default";
  else if (item.status == 2 || item.status == 3 || item.status == 6) badgeStatus = "error";
  else if (item.paused == 1) badgeStatus = "warning";
  else badgeStatus = "success";

  const getInitials = (name) => {
    if (!name) return "";
    if (/^\d+$/.test(name.replace(/\s/g, ""))) return null;

    const words = name.trim().split(/\s+/);
    if (words.length >= 2) {
      return (words[0][0] + words[words.length - 1][0]).toUpperCase();
    }
    return words[0].substring(0, 2).toUpperCase();
  };

  const colors = ["#f56a00", "#7265e6", "#ffbf00", "#00a2ae", "#1890ff"];
  const colorIndex = (item.endpoint_id || 0) % colors.length;
  const initials = getInitials(item.name);

  return (
    <div className="agent-avatar-wrapper">
      <Badge dot offset={[-4, 32]} status={badgeStatus}>
        <Avatar
          size={48}
          icon={(imageError || !avatarUrl) && !initials ? <UserOutlined /> : (!imageError ? <UserOutlined /> : null)}
          src={(!imageError && avatarUrl) ? avatarUrl : null}
          style={imageError || !avatarUrl ? { backgroundColor: colors[colorIndex], verticalAlign: 'middle' } : {}}
          onError={() => {
            setImageError(true);
            return true;
          }}
        >
          {(imageError || !avatarUrl) && initials}
        </Avatar>
      </Badge>
    </div>
  );
});

const AgentCard = React.memo(({ item, isBranch }) => {
  const statusInfo = getStatusInfo(item);
  const stats = item.totalCallLog || {};

  return (
    <div className={`agent-card ${statusInfo.class}`}>
      <div className="agent-main-content">
        <AgentAvatar item={item} />
        <div className="agent-info">
          <div className="agent-header-row">
            <Text className="agent-name" title={item.name}>
              {item.name}
            </Text>
          </div>
          <div className="agent-meta">
            <span className="agent-ext-badge">#{item.ext}</span>
            <span className="agent-dept-text">{item.department_name || "No Dept"}</span>
          </div>
          <div className={`agent-status-tag ${statusInfo.tag}`}>
            {statusInfo.icon} <span style={{ marginLeft: 4 }}>{item.statusName || statusInfo.label}</span>
          </div>
        </div>
      </div>

      {isBranch ? (
        <div className="agent-branch-stats">
          <div className="branch-stat-item">
            <span className="branch-stat-label">Tổng</span>
            <span className="branch-stat-value text-primary">{stats.tong_goi_vao || 0}</span>
          </div>
          <div className="branch-stat-item">
            <span className="branch-stat-label">Đã nghe</span>
            <span className="branch-stat-value text-success">{stats.goi_vao_ket_noi || 0}</span>
          </div>
          <div className="branch-stat-item">
            <span className="branch-stat-label">Nhỡ</span>
            <span className="branch-stat-value text-error">{stats.goi_vao_miss || 0}</span>
          </div>
          <div className="branch-stat-item">
            <span className="branch-stat-label">Chuyển</span>
            <span className="branch-stat-value text-info">{stats.goi_vao_ket_noi_chuyen_cn || 0}</span>
          </div>
        </div>
      ) : (
        <div className="agent-call-stats">
          <div className="agent-call-stat-item inbound">
            <div className="stat-icon-wrapper">
              <ArrowDownOutlined className="stat-icon" />
            </div>
            <div className="agent-stat-info">
              <div className="agent-stat-header">
                <span className="agent-stat-label">Vào:</span>
                <span className="agent-stat-value">{stats.goi_vao_ket_noi || 0}</span>
              </div>
              <span className="agent-stat-duration">{formatDuration(stats.goi_vao_talktime)}</span>
            </div>
          </div>
          <div className="agent-call-stat-item outbound">
            <div className="stat-icon-wrapper">
              <ArrowUpOutlined className="stat-icon" />
            </div>
            <div className="agent-stat-info">
              <div className="agent-stat-header">
                <span className="agent-stat-label">Ra:</span>
                <span className="agent-stat-value">{stats.goi_ra_ket_noi || 0}</span>
              </div>
              <span className="agent-stat-duration">{formatDuration(stats.goi_ra_talktime)}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

export default function ShowTable({
  data,
  setRefressSearch,
  departmentSelected,
  exten,
  agentStatus,
}) {
  const { departmentList, totalCallLog: totalLogArray } = data || {};
  const summary = totalLogArray?.[0] || {
    tong_goi_vao: "0", goi_vao_ket_noi: "0", goi_vao_miss: "0",
    tong_goi_ra: "0", goi_ra_ket_noi: "0", goi_ra_miss: "0"
  };

  const isShowAgent = (item) => {
    if (!departmentSelected.includes(+item.department_id) && item.department_id != "0") return false;
    if (exten && exten.length > 0 && !exten.includes(item.ext)) return false;

    switch (agentStatus) {
      case "5": return item.status == 5;
      case "Online": return item.status != 5;
      case "Incall": return (item.status == 2 || item.status == 3 || item.status == 6);
      case "Idle": return (item.status === "1" && item.paused === "0");
      case "Paused": return item.paused == "1";
      default: return true;
    }
  };

  const centralDepartments = [];
  const branchAgents = [];

  if (departmentList) {
    Object.entries(departmentList).forEach(([deptId, dept]) => {
      const isBranchDept = dept.agents.length === 1;
      if (isBranchDept) {
        dept.agents.forEach(agent => {
          if (isShowAgent(agent)) {
            // Always use department totalCallLog for branch agents
            const agentWithBranchStats = {
              ...agent,
              totalCallLog: dept.totalCallLog || {}
            };
            branchAgents.push(agentWithBranchStats);
          }
        });
      } else {
        centralDepartments.push({ deptId, dept });
      }
    });
  }

  return (
    <div className="agent-status-container">
      <div className="stats-container">
        <div className="stat-group">
          <div className="stat-header"><i className="fas fa-arrow-down"></i> Cuộc gọi đầu vào</div>
          <div className="stat-row">
            <div className="stat-item">
              <div className="stat-value" style={{ color: "#007bff" }}>{summary.tong_goi_vao}</div>
              <div className="stat-label">Tổng số</div>
            </div>
            <div className="stat-item">
              <div className="stat-value text-out" style={{ color: "#28a745" }}>{summary.goi_vao_ket_noi}</div>
              <div className="stat-label">Tiếp nhận</div>
            </div>
            <div className="stat-item">
              <div className="stat-value text-miss" style={{ color: "#dc3545" }}>{summary.goi_vao_miss}</div>
              <div className="stat-label">Nhỡ</div>
            </div>
          </div>
        </div>
        <div className="stat-group">
          <div className="stat-header" style={{ backgroundColor: "#17a2b8" }}><i className="fas fa-arrow-up"></i> Cuộc gọi đầu ra</div>
          <div className="stat-row">
            <div className="stat-item">
              <div className="stat-value" style={{ color: "#17a2b8" }}>{summary.tong_goi_ra}</div>
              <div className="stat-label">Tổng số</div>
            </div>
            <div className="stat-item">
              <div className="stat-value text-out" style={{ color: "#28a745" }}>{summary.goi_ra_ket_noi}</div>
              <div className="stat-label">Thành công</div>
            </div>
            <div className="stat-item">
              <div className="stat-value text-miss" style={{ color: "#dc3545" }}>{summary.goi_ra_miss}</div>
              <div className="stat-label">Không TC</div>
            </div>
          </div>
        </div>
      </div>

      <div className="department-grid-container" style={{ marginTop: '20px' }}>
        {/* Central Departments */}
        {centralDepartments.map(({ deptId, dept }) => (
          <div key={deptId} className="department-section" style={{ marginBottom: '30px' }}>
            <div className="department-header-title" style={{ marginBottom: '15px', padding: '8px', background: '#f0f2f5', borderRadius: '4px' }}>
              <Typography.Text strong style={{ fontSize: '16px' }}>
                <i className="fas fa-building" style={{ marginRight: '8px' }}></i>
                {dept.info.name}
              </Typography.Text>
            </div>
            <div className="agent-grid">
              {dept.agents.map((agent, index) => {
                if (!isShowAgent(agent)) return null;
                return (
                  <AgentCard
                    key={agent.endpoint_id || index}
                    item={agent}
                    isBranch={false}
                  />
                );
              })}
            </div>
          </div>
        ))}

        {/* Branch Agents Group */}
        {branchAgents.length > 0 && (
          <div className="department-section" style={{ marginBottom: '30px' }}>
            <div className="department-header-title" style={{ marginBottom: '15px', padding: '8px', background: '#f0f2f5', borderRadius: '4px' }}>
              <Typography.Text strong style={{ fontSize: '16px' }}>
                <i className="fas fa-code-branch" style={{ marginRight: '8px' }}></i>
                Tổng đài chi nhánh
              </Typography.Text>
            </div>
            <div className="agent-grid">
              {branchAgents.map((agent, index) => (
                <AgentCard
                  key={agent.endpoint_id || index}
                  item={agent}
                  isBranch={true}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
